import { Link, useLocation, useNavigate, useNavigation } from 'react-router'
import type { ReactElement } from 'react'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

import { getThemePreviewImagePath } from '../../../utils/themes'
import { withQueryAndScope } from '../../urlGeneratorsPlain'
import VideoTutorialButton from '../videoTutorials/VideoTutorialButton'

export default function ThemeManager({ t }: Readonly<TranslateProps>): ReactElement | null {
  const themes = useSelector<State, Core.Theme[]>((state: State) => state.get('themes')?.toJS())
  const isBeyondShop = useSelector((state: State) => Boolean(state.getIn(['shop', 'beyond'])))
  const shopTheme = useSelector<State, Core.Theme>((state: State) => state.getIn(['shop', 'theme']).toJS())
  const hashedThemeAssets = useSelector<State, State>((state) => state.getIn(['view', 'hashedThemeAssets']))
  const isTutorialVideoAvailable = useSelector<State, boolean>(
    // Tutorial videos are only available for German merchants at the moment.
    (state) => ['de'].includes(state.getIn(['view', 'interfaceLanguage'])),
  )

  const location = useLocation()
  const navigation = useNavigation()

  // Sync display of shop theme with location change on theme switch.
  const [currentShopTheme, setCurrentShopTheme] = useState(shopTheme)
  useEffect(() => {
    if (navigation.state === 'idle' && shopTheme.id !== currentShopTheme.id) {
      setCurrentShopTheme(shopTheme)
    }
  }, [navigation.state, shopTheme, currentShopTheme])

  const currentPathname = location.pathname
  const themesViewPath = '/editor/themes'
  const editorPath = '/editor'
  const isOnThemesView = currentPathname === themesViewPath
  const navigate = useNavigate()

  // TODO:
  // Remove this effect when all links to #themeSelection are replaced with deep links to /editor/themes.
  // See also https://github.com/ePages-de/ng-merchant-ui/pull/2641
  useEffect(() => {
    if (window.location.hash === '#themeSelection') navigate(withQueryAndScope('/themes', location))
  }, [navigate, location])

  if (!themes) return null

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const activeTheme = themes.find((theme) => theme.id === currentShopTheme.id)!
  const demoUrl = (activeTheme?.demoUrls ?? {})[isBeyondShop ? 'beyond' : 'now']

  return (
    <div className="ep-sidebar-module-content">
      <div className="ep-scrollable">
        <h4 className="ep-theme-manager-heading">{`${activeTheme.displayName}`}</h4>
        <div className="ep-theme-manager-preview-image">
          <img alt={activeTheme.displayName} src={getThemePreviewImagePath(activeTheme.name, hashedThemeAssets)} />
        </div>
        <div className="ep-theme-manager-change-theme-button-wrapper">
          <Link to={isOnThemesView ? editorPath : themesViewPath} className="ep-theme-manager-change-theme-button">
            {t(`${isOnThemesView ? 'backToEditorButton' : 'changeThemeButton'}.label`)}
          </Link>
        </div>
        {demoUrl && (
          <div className="ep-theme-manager-demo-shop-link-wrapper">
            <a href={demoUrl} className="ep-theme-manager-demo-shop-link" target="_blank" rel="noopener noreferrer">
              {t('demoShopLink')}
            </a>
          </div>
        )}
        {!isBeyondShop && isTutorialVideoAvailable && (
          <div className="ep-theme-manager-video-tutorial-wrapper">
            <hr className="ep-theme-manager-video-tutorial-divider" />
            <VideoTutorialButton tutorial="themes">{t('videoTutorial.videoTutorialButton.label')}</VideoTutorialButton>
          </div>
        )}
      </div>
    </div>
  )
}
